import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SitePlannerViewer from '../Viewer/SitePlannerViewer';
import SitePlannerPanel from '../Viewer/SitePlannerPanel';
import ViewerChrome from '../Viewer/ViewerChrome';
import Viewer from '../Viewer/Viewer';
import R3FModelViewer from '../Viewer/R3FModelViewer';
import {
  geocodeAddress,
  fetchSatelliteGround,
  fetchGroundElevation,
  DEFAULT_GROUND_SQ_MI,
  sqMiToSideMeters,
} from '../../services/googleMapsSite';
import {
  fetchOsmBuildings,
  rectangleVertices,
  polygonCentroid,
} from '../../services/osmBuildings';
import {
  pushLiveVersion,
  createLiveSyncScheduler,
  isCaptureTilesEnabled,
  invalidateContextCache,
  getLiveSyncConfig,
  listProjectModels,
  createNamedLiveModel,
  persistLiveModelUrl,
  ensureLiveModel,
  DEFAULT_LIVE_MODEL_NAME,
} from '../../services/speckleLiveSync';
import { clearFloorCache } from '../../services/photorealisticFloor';
import {
  getTileSnapshotBudget,
  ladderNoteForStep,
  nextLadderAfterTilesFail,
} from '../../services/photorealisticLoadLadder';
import {
  DEFAULT_SITE_PLANNER_MODEL,
  catalogEntry,
  catalogLabel,
} from '../../data/sitePlannerCatalog';
import {
  preloadCatalogModel,
  preloadCatalogQueue,
} from '../../services/catalogModelPreload';

function newId() {
  return `s_${Math.random().toString(36).slice(2, 9)}`;
}

const DEFAULT_LOT = { vertices: rectangleVertices(0, 0, 18, 22), y: 0 };

/**
 * /viewer — site planner (default R3F), model-only R3F, or Speckle stream.
 * Docs: docs/ADU_SITE_PLANNER.md, docs/R3F_VIEWER.md
 */
function SpeckleViewerPage() {
  const [params] = useSearchParams();
  const modeParam = params.get('mode');
  const initialMode =
    modeParam === 'speckle'
      ? 'speckle'
      : modeParam === 'model'
        ? 'model'
        : 'site';
  const urlFromQuery = params.get('url') || undefined;
  const [viewerMode, setViewerMode] = useState(initialMode);

  const [address, setAddress] = useState('');
  const [groundSqMi, setGroundSqMi] = useState(DEFAULT_GROUND_SQ_MI);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState(null);
  const [site, setSite] = useState(null);
  const [lot, setLot] = useState(DEFAULT_LOT);
  const [mapBuildings, setMapBuildings] = useState([]);
  const [contextMode, setContextMode] = useState('google');
  /** Photorealistic snapshot: 0=full → 1=half → 2=quarter. */
  const [tileSnapshotLevel, setTileSnapshotLevel] = useState(0);
  const [contextNote, setContextNote] = useState(null);
  const [viewLocked, setViewLocked] = useState(false);
  const [lotSelected, setLotSelected] = useState(false);
  /** null = waiting for Photorealistic floor lock (lot/ADUs hidden). */
  const [floorY, setFloorY] = useState(null);
  const [floorDebug, setFloorDebug] = useState(null);
  const [structures, setStructures] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [layers, setLayers] = useState({
    roof: true,
    walls: true,
    interior: true,
  });
  const [section, setSection] = useState({ enabled: false, offset: 0 });
  const [plannerStage, setPlannerStage] = useState('site');
  const [catalogType, setCatalogType] = useState(DEFAULT_SITE_PLANNER_MODEL);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  /** slug → true while selected model is eagerly preloading */
  const [preloadSlug, setPreloadSlug] = useState(null);
  /** structureId → type while that instance Suspense is unresolved */
  const [loadingStructures, setLoadingStructures] = useState({});
  const [liveSyncEnabled, setLiveSyncEnabled] = useState(false);
  const [liveSyncBusy, setLiveSyncBusy] = useState(false);
  const [liveSyncStatus, setLiveSyncStatus] = useState(null);
  const [liveModels, setLiveModels] = useState([]);
  const [liveModelId, setLiveModelId] = useState(
    () => getLiveSyncConfig().modelId || ''
  );
  const [tileCredits, setTileCredits] = useState('');
  const liveStateRef = useRef({});
  const liveModelIdRef = useRef(liveModelId);
  const localizeRef = useRef(null);
  const liveCfg = useMemo(() => getLiveSyncConfig(), []);

  useEffect(() => {
    liveModelIdRef.current = liveModelId;
  }, [liveModelId]);

  // Eager-preload the selected catalog model as soon as the user picks it.
  useEffect(() => {
    if (!catalogType) return undefined;
    let cancelled = false;
    setPreloadSlug(catalogType);
    preloadCatalogModel(catalogType)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setPreloadSlug((cur) => (cur === catalogType ? null : cur));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogType]);

  // Background queue: Construx SKUs first, then SoftPlan examples (concurrency 2).
  useEffect(() => {
    const ac = new AbortController();
    preloadCatalogQueue({ concurrency: 2, signal: ac.signal }).catch(() => {});
    return () => ac.abort();
  }, []);

  const onStructureLoadChange = useCallback((id, loading, type) => {
    setLoadingStructures((prev) => {
      if (loading) {
        if (prev[id] === type) return prev;
        return { ...prev, [id]: type || true };
      }
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const modelLoadLabel = useMemo(() => {
    const ids = Object.keys(loadingStructures);
    if (ids.length) {
      const type = loadingStructures[ids[ids.length - 1]];
      return typeof type === 'string' ? catalogLabel(type) : 'model';
    }
    if (preloadSlug) return catalogLabel(preloadSlug);
    return null;
  }, [loadingStructures, preloadSlug]);

  useEffect(() => {
    liveStateRef.current = {
      site,
      lot,
      structures,
      heightOffset: Number(lot?.y) || 0,
      googleAttribution: tileCredits,
    };
  }, [site, lot, structures, tileCredits]);

  const refreshLiveModels = useCallback(async () => {
    const cfg = getLiveSyncConfig();
    if (!cfg.configured) {
      setLiveSyncStatus({
        error: 'Set REACT_APP_SPECKLE_TOKEN and a project URL to list models.',
        versionUrl: null,
        info: null,
      });
      return [];
    }
    try {
      const models = await listProjectModels({
        serverUrl: cfg.serverUrl,
        projectId: cfg.projectId,
        token: cfg.token,
      });
      setLiveModels(models);

      let nextId = liveModelIdRef.current;
      if (nextId && !models.some((m) => m.id === nextId)) {
        nextId = '';
      }
      if (!nextId) {
        const preferred =
          models.find((m) => m.name === DEFAULT_LIVE_MODEL_NAME) || models[0];
        if (preferred) {
          nextId = preferred.id;
          persistLiveModelUrl(preferred.url);
        } else {
          const ensured = await ensureLiveModel({
            serverUrl: cfg.serverUrl,
            projectId: cfg.projectId,
            token: cfg.token,
            name: DEFAULT_LIVE_MODEL_NAME,
          });
          nextId = ensured.modelId;
          const again = await listProjectModels({
            serverUrl: cfg.serverUrl,
            projectId: cfg.projectId,
            token: cfg.token,
          });
          setLiveModels(again);
        }
      }
      setLiveModelId(nextId || '');
      return models;
    } catch (err) {
      setLiveSyncStatus({
        error: err?.message || String(err),
        versionUrl: null,
        info: null,
      });
      return [];
    }
  }, []);

  useEffect(() => {
    refreshLiveModels();
  }, [refreshLiveModels]);

  const onLiveModelChange = useCallback((id) => {
    setLiveModelId(id);
    const cfg = getLiveSyncConfig();
    if (id && cfg.projectId) {
      persistLiveModelUrl(
        `${cfg.serverUrl}/projects/${cfg.projectId}/models/${id}`
      );
    }
  }, []);

  const onCreateLiveModel = useCallback(async () => {
    const cfg = getLiveSyncConfig();
    if (!cfg.configured) return;
    setLiveSyncBusy(true);
    try {
      const created = await createNamedLiveModel({
        serverUrl: cfg.serverUrl,
        projectId: cfg.projectId,
        token: cfg.token,
      });
      await refreshLiveModels();
      setLiveModelId(created.modelId);
      persistLiveModelUrl(created.url);
      setLiveSyncStatus({
        info: `Created model “${created.name}”`,
        error: null,
        versionUrl: null,
      });
    } catch (err) {
      setLiveSyncStatus({
        error: err?.message || String(err),
        versionUrl: null,
        info: null,
      });
    } finally {
      setLiveSyncBusy(false);
    }
  }, [refreshLiveModels]);

  const runLiveSync = useCallback(async ({ forceContext = false, message } = {}) => {
    setLiveSyncBusy(true);
    setLiveSyncStatus((s) => ({ ...s, error: null }));
    try {
      const result = await pushLiveVersion(liveStateRef.current, {
        forceContext,
        message,
        modelId: liveModelIdRef.current || undefined,
      });
      if (result?.modelUrl) {
        const mid = result.modelUrl.split('/models/')[1]?.split('@')[0];
        if (mid && mid !== liveModelIdRef.current) {
          setLiveModelId(mid);
        }
      }
      setLiveSyncStatus({
        versionUrl: result.versionUrl,
        modelUrl: result.modelUrl,
        info: result.context
          ? `Context ${Math.round(result.context.triangles)} tris · r=${result.context.radiusM.toFixed(0)}m`
          : result.contextWarning
            ? `Scene graph synced · context pending: ${result.contextWarning}`
            : 'Scene graph synced',
        error: null,
      });
      return result;
    } catch (err) {
      setLiveSyncStatus({
        error: err?.message || String(err),
        versionUrl: null,
        info: null,
      });
      return null;
    } finally {
      setLiveSyncBusy(false);
    }
  }, []);

  const liveScheduler = useMemo(
    () =>
      createLiveSyncScheduler(async () => {
        if (!liveSyncEnabled) return null;
        return runLiveSync();
      }, 500),
    [liveSyncEnabled, runLiveSync]
  );

  const onLiveSyncEnabledChange = useCallback(
    async (on) => {
      setLiveSyncEnabled(on);
      if (on) {
        await runLiveSync({
          forceContext: isCaptureTilesEnabled(),
          message: 'Live sync enabled',
        });
      }
    },
    [runLiveSync]
  );

  useEffect(() => {
    return () => {
      if (site?.objectUrl) URL.revokeObjectURL(site.objectUrl);
    };
  }, [site?.objectUrl]);

  // OSM step with no footprints → default 3D (ladder step 5).
  useEffect(() => {
    if (contextMode !== 'osm') return;
    if (mapBuildings.length > 0) return;
    setContextMode('off');
    setFloorY((y) => (y == null || !Number.isFinite(y) ? 0 : y));
    setContextNote(ladderNoteForStep('off'));
  }, [contextMode, mapBuildings.length]);

  const loadSite = useCallback(async () => {
    setSiteError(null);
    setSiteLoading(true);
    const groundMeters = sqMiToSideMeters(groundSqMi);
    try {
      const geo = await geocodeAddress(address.trim());
      let elevationMeters = 0;
      try {
        elevationMeters = await fetchGroundElevation(geo.lat, geo.lng);
      } catch (elevErr) {
        console.warn('Ground elevation unavailable', elevErr);
      }
      const sat = await fetchSatelliteGround(geo.lat, geo.lng, groundMeters);
      let buildings = [];
      try {
        buildings = await fetchOsmBuildings(
          geo.lat,
          geo.lng,
          sat.groundMeters / 2
        );
      } catch (osmErr) {
        console.warn('OSM buildings unavailable', osmErr);
      }
      setSite((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return {
          ...geo,
          elevationMeters,
          objectUrl: sat.objectUrl,
          groundMeters: sat.groundMeters,
          viewMeters: groundMeters,
          groundSqMi,
          zoom: sat.zoom,
        };
      });
      setMapBuildings(buildings);
      clearFloorCache();
      setFloorY(null);
      setFloorDebug(null);
      setTileSnapshotLevel(0);
      setContextMode('google');
      setContextNote(null);
      setLot(DEFAULT_LOT);
      setStructures([]);
      setSelectedId(null);
      setViewLocked(false);
      setLotSelected(false);
    } catch (err) {
      setSiteError(err?.message || String(err));
    } finally {
      setSiteLoading(false);
    }
  }, [address, groundSqMi]);

  const onTilesAttemptFailed = useCallback(
    (payload) => {
      const step = nextLadderAfterTilesFail(
        tileSnapshotLevel,
        mapBuildings.length
      );
      console.warn('Photorealistic ladder escalate', payload, step);
      clearFloorCache();
      if (step.kind === 'tiles') {
        setTileSnapshotLevel(step.levelIndex);
        setFloorY(null);
        setContextMode('google');
        setContextNote(
          ladderNoteForStep('tiles', getTileSnapshotBudget(step.levelIndex).id)
        );
        return;
      }
      if (step.kind === 'osm') {
        setContextMode('osm');
        setFloorY(0);
        setContextNote(ladderNoteForStep('osm'));
        return;
      }
      setContextMode('off');
      setFloorY(0);
      setContextNote(ladderNoteForStep('off'));
    },
    [tileSnapshotLevel, mapBuildings.length]
  );

  const applyGroundSqMi = useCallback(
    async (nextSqMi) => {
      setGroundSqMi(nextSqMi);
      if (!site?.lat) return;
      const groundMeters = sqMiToSideMeters(nextSqMi);
      // Photorealistic tiles are global — only reframe the camera via groundMeters
      if (contextMode === 'google') {
        setSite((prev) =>
          prev
            ? {
                ...prev,
                viewMeters: groundMeters,
                groundSqMi: nextSqMi,
              }
            : prev
        );
        return;
      }
      try {
        const sat = await fetchSatelliteGround(
          site.lat,
          site.lng,
          groundMeters
        );
        setSite((prev) => {
          if (!prev) return prev;
          if (prev.objectUrl) URL.revokeObjectURL(prev.objectUrl);
          return {
            ...prev,
            objectUrl: sat.objectUrl,
            groundMeters: sat.groundMeters,
            viewMeters: groundMeters,
            groundSqMi: nextSqMi,
            zoom: sat.zoom,
          };
        });
      } catch (err) {
        console.warn('Ground retile failed', err);
      }
    },
    [site?.lat, site?.lng, contextMode]
  );

  const resetLotRectangle = useCallback(
    (width = 18, depth = 22) => {
      setLot({ vertices: rectangleVertices(0, 0, width, depth), y: 0 });
      if (liveSyncEnabled) liveScheduler.schedule();
    },
    [liveSyncEnabled, liveScheduler]
  );

  const resetLotFloor = useCallback(() => {
    // Hide content, re-raycast tiles, re-lock floor, then re-show (structures kept).
    clearFloorCache();
    setFloorY(null);
    setFloorDebug(null);
    setLot((prev) => ({ ...prev, y: 0 }));
    setLotSelected(false);
    if (liveSyncEnabled) liveScheduler.schedule();
  }, [liveSyncEnabled, liveScheduler]);

  const onFloorLock = useCallback((y) => {
    const next = Number(y);
    if (!Number.isFinite(next)) return;
    setFloorY((prev) => {
      if (prev == null || !Number.isFinite(prev)) return next;
      // Allow downward refinement (roof → grade) after the first lock.
      if (next < prev - 0.5) return next;
      return prev;
    });
  }, []);

  const onBindLocalize = useCallback((fn) => {
    localizeRef.current = fn;
  }, []);

  // Debug only — post-lock downward floor steps removed (lot fall-through).
  // See docs/ADU_SITE_PLANNER.md · Fixed load camera.
  const onFloorDebug = useCallback((debug) => {
    setFloorDebug(debug);
  }, []);

  const onLotChange = useCallback(
    (next) => {
      setLot((prev) => ({
        vertices: next.vertices ?? prev.vertices,
        y: next.y !== undefined ? Number(next.y) || 0 : prev.y || 0,
      }));
      if (liveSyncEnabled) liveScheduler.schedule();
    },
    [liveSyncEnabled, liveScheduler]
  );

  const addStructure = useCallback(
    (type = DEFAULT_SITE_PLANNER_MODEL) => {
      const slug = catalogEntry(type)?.slug || DEFAULT_SITE_PLANNER_MODEL;
      // Kick preload before mount so Suspense time is shorter when possible.
      preloadCatalogModel(slug).catch(() => {});
      const id = newId();
      const [bx, bz] = polygonCentroid(lot.vertices);
      setStructures((list) => [
        ...list,
        {
          id,
          type: slug,
          position: [bx, 0, bz],
          rotationY: 0,
        },
      ]);
      setSelectedId(id);
      setLotSelected(false);
      if (liveSyncEnabled) liveScheduler.schedule();
    },
    [lot.vertices, liveSyncEnabled, liveScheduler]
  );

  const removeStructure = useCallback(
    (id) => {
      setStructures((list) => list.filter((s) => s.id !== id));
      setSelectedId((cur) => (cur === id ? null : cur));
      setLoadingStructures((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (liveSyncEnabled) liveScheduler.schedule();
    },
    [liveSyncEnabled, liveScheduler]
  );

  const updateStructure = useCallback(
    (id, patch) => {
      setStructures((list) =>
        list.map((s) => (s.id === id ? { ...s, ...patch } : s))
      );
      if (liveSyncEnabled) liveScheduler.schedule();
    },
    [liveSyncEnabled, liveScheduler]
  );

  const showSpeckle = viewerMode === 'speckle';
  const showModelOnly = viewerMode === 'model';
  const showSite = viewerMode === 'site';
  const configured = Boolean(
    urlFromQuery || process.env.REACT_APP_SPECKLE_R1AS_URL
  );
  const siteContentReady = contextMode !== 'google' || floorY != null;
  /** Geocode + Photorealistic pin LODs + floor lock — global chrome loader. */
  const siteBootLoading =
    siteLoading ||
    (Boolean(site?.lat) &&
      contextMode === 'google' &&
      floorY == null);

  const tileBudgetLabel = getTileSnapshotBudget(tileSnapshotLevel).id;
  const siteBootTitle = siteLoading
    ? 'Finding your address'
    : tileBudgetLabel === 'full'
      ? 'Building the neighborhood'
      : 'Trying a lighter 3D map';
  const siteBootDetail = siteLoading
    ? 'Geocoding and preparing ground…'
    : contextNote && tileBudgetLabel !== 'full'
      ? contextNote
      : floorDebug?.meshCount
        ? `Streaming Photorealistic tiles… ${floorDebug.meshCount} meshes${
            floorDebug.waitSec != null ? ` · ${floorDebug.waitSec}s` : ''
          }${tileBudgetLabel !== 'full' ? ` · ${tileBudgetLabel}` : ''}`
        : 'Streaming Photorealistic tiles around the lot…';

  const primaryForStage = () => {
    if (plannerStage === 'site') {
      return {
        label: siteLoading ? 'Loading…' : site ? 'Place ADU' : 'Load site',
        disabled: site
          ? !siteContentReady
          : siteLoading || !address.trim(),
        action: () => {
          if (!site) loadSite();
          else setPlannerStage('place');
        },
      };
    }
    if (plannerStage === 'place') {
      return {
        label: 'Add ADU',
        disabled: !site || !siteContentReady,
        action: () => addStructure(catalogType),
      };
    }
    if (plannerStage === 'inspect') {
      return {
        label: 'Next',
        disabled: false,
        action: () => setPlannerStage('next'),
      };
    }
    if (plannerStage === 'tools') {
      return {
        label: liveSyncBusy ? 'Syncing…' : 'Sync now',
        disabled: !site || !liveModelId || liveSyncBusy,
        action: () => runLiveSync({ message: 'Manual sync' }),
      };
    }
    return {
      label: 'Browse models',
      disabled: false,
      action: () => {
        window.location.href = '/models';
      },
    };
  };
  const primary = primaryForStage();

  const panelProps = {
    stage: plannerStage === 'tools' ? 'tools' : plannerStage,
    address,
    onAddressChange: setAddress,
    onLoadSite: loadSite,
    siteLoading,
    siteError: siteError || contextNote,
    site,
    groundSqMi,
    onGroundSqMiChange: applyGroundSqMi,
    lot,
    onLotChange,
    onResetLot: resetLotRectangle,
    onResetLotFloor: resetLotFloor,
    floorDebug,
    floorY,
    viewLocked,
    onViewLockedChange: setViewLocked,
    lotSelected,
    onSelectLot: () => setLotSelected(true),
    mapBuildingCount: mapBuildings.length,
    contextMode,
    onContextModeChange: (mode) => {
      setContextMode(mode);
      setContextNote(null);
      if (mode === 'google') {
        clearFloorCache();
        setFloorY(null);
        setTileSnapshotLevel(0);
      } else {
        setFloorY(0);
      }
    },
    siteContentReady,
    structures,
    selectedId,
    onSelectStructure: setSelectedId,
    onAddStructure: addStructure,
    onRemoveStructure: removeStructure,
    layers,
    onLayersChange: setLayers,
    section,
    onSectionChange: setSection,
    catalogType,
    onCatalogTypeChange: setCatalogType,
    liveSyncEnabled,
    onLiveSyncEnabledChange: onLiveSyncEnabledChange,
    liveSyncStatus,
    liveSyncBusy,
    captureTilesEnabled: isCaptureTilesEnabled(),
    liveModels,
    liveModelId,
    onLiveModelChange,
    onRefreshLiveModels: refreshLiveModels,
    onCreateLiveModel,
    liveProjectUrl: liveCfg.projectUrl,
    onLiveSyncNow: () => runLiveSync({ message: 'Manual sync' }),
    onRecaptureContext: () => {
      invalidateContextCache();
      return runLiveSync({
        forceContext: true,
        message: 'Recapture Photorealistic context',
      });
    },
  };

  if (showSite) {
    return (
      <ViewerChrome
        stage={plannerStage === 'tools' ? 'tools' : plannerStage}
        onStageChange={(s) => {
          if (s === 'place' && !site) setPlannerStage('site');
          else setPlannerStage(s);
        }}
        site={site}
        structureCount={structures.length}
        siteContentReady={siteContentReady}
        viewLocked={viewLocked}
        onViewLockedChange={setViewLocked}
        sectionEnabled={section.enabled}
        onToggleSection={() =>
          setSection((prev) => ({ ...prev, enabled: !prev.enabled }))
        }
        onOpenLayers={() => setPlannerStage('inspect')}
        onOpenContext={() => setPlannerStage('site')}
        onOpenSync={() => setPlannerStage('tools')}
        onAddStructure={() => addStructure(catalogType)}
        primaryLabel={primary.label}
        primaryDisabled={primary.disabled}
        onPrimaryAction={primary.action}
        sheetCollapsed={sheetCollapsed}
        onSheetCollapsedChange={setSheetCollapsed}
        modelLoadLabel={modelLoadLabel}
        siteBootLoading={siteBootLoading}
        siteBootTitle={siteBootTitle}
        siteBootDetail={siteBootDetail}
        onLocalize={() => localizeRef.current?.()}
        toolsMenu={
          <>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-warm-50"
              onClick={() => setViewerMode('model')}
            >
              Model only
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-warm-50"
              onClick={() => setViewerMode('speckle')}
            >
              Speckle stream
            </button>
          </>
        }
        sheet={<SitePlannerPanel {...panelProps} />}
      >
        <SitePlannerViewer
          heightClass="h-full"
          site={site}
          lot={lot}
          onLotChange={onLotChange}
          mapBuildings={mapBuildings}
          contextMode={contextMode}
          tileSnapshotLevel={tileSnapshotLevel}
          onTilesAttemptFailed={onTilesAttemptFailed}
          viewLocked={viewLocked}
          onViewLockedChange={setViewLocked}
          lotSelected={lotSelected}
          onLotSelectedChange={setLotSelected}
          floorY={floorY}
          onFloorLock={onFloorLock}
          onFloorDebug={onFloorDebug}
          floorDebug={floorDebug}
          structures={structures}
          selectedId={selectedId}
          onSelectStructure={setSelectedId}
          onUpdateStructure={updateStructure}
          layers={layers}
          section={section}
          onTileCredits={setTileCredits}
          onStructureLoadChange={onStructureLoadChange}
          onBindLocalize={onBindLocalize}
        />
      </ViewerChrome>
    );
  }

  const modeBtn = (active) =>
    `px-3 py-1.5 text-sm rounded-[10px] border transition-colors ${
      active
        ? 'bg-gray-900 text-white border-gray-900'
        : 'bg-white border-warm-200 hover:bg-warm-50'
    }`;

  return (
    <div className="min-h-screen bg-warm-50 font-bai">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            type="button"
            className={modeBtn(false)}
            onClick={() => setViewerMode('site')}
          >
            ← Site planner
          </button>
          <button
            type="button"
            className={modeBtn(showModelOnly)}
            onClick={() => setViewerMode('model')}
          >
            Model only
          </button>
          <button
            type="button"
            className={modeBtn(showSpeckle)}
            onClick={() => setViewerMode('speckle')}
          >
            Speckle stream
          </button>
        </div>

        {showSpeckle && (
          <>
            {!configured && (
              <div className="mb-6 p-4 border border-amber-300 bg-amber-50 text-amber-950 text-sm rounded-md">
                Set{' '}
                <code className="text-xs bg-white/80 px-1 rounded">
                  REACT_APP_SPECKLE_R1AS_URL
                </code>{' '}
                or pass{' '}
                <code className="text-xs bg-white/80 px-1 rounded">
                  ?mode=speckle&amp;url=…
                </code>
                .
              </div>
            )}
            <Viewer resourceUrl={urlFromQuery} />
          </>
        )}

        {showModelOnly && <R3FModelViewer />}

        <p className="mt-6 text-sm text-gray-500">
          <Link to="/models" className="underline hover:text-gray-800">
            Model catalog
          </Link>
          {' · '}
          See <code className="text-xs">docs/ADU_SITE_PLANNER.md</code>
        </p>
      </div>
    </div>
  );
}

export default SpeckleViewerPage;
